import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { HelpTip } from "@/components/HelpTip";
import { PartnerBenefitsPanel } from "@/components/admin/PartnerBenefitsPanel";
import { useFormats } from "@/lib/useFormats";

const STATUSES = ["STANDARD", "PREFERRED", "PREMIER", "STRATEGIC"];

export default function DealerPricingTab({ dealerId }) {
  const fmts = useFormats();
  const FORMATS = fmts.map((f) => f.code);
  const FMT_LABEL = Object.fromEntries(fmts.map((f) => [f.code, f.label]));
  const [data, setData] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "STANDARD", reason: "" });
  const [ovr, setOvr] = useState({ prices: {}, premiums_apply: false, active: false, notes: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/dealers/${dealerId}/pricing`);
      setData(data);
      const o = data.override || {};
      setOvr({
        prices: o.format_prices || {},
        premiums_apply: !!o.premiums_apply,
        active: o.active !== false && !!o.format_prices,
        notes: o.notes || "",
      });
      setStatusForm((f) => ({ ...f, status: data.partner_status?.status || "STANDARD" }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [dealerId]);

  const saveStatus = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/dealers/${dealerId}/partner-status`, statusForm);
      toast.success("Partner status updated — audited");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const saveOverride = async () => {
    setSaving(true);
    try {
      const format_prices = Object.fromEntries(
        Object.entries(ovr.prices).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, parseFloat(v)])
      );
      await api.put(`/admin/dealers/${dealerId}/pricing`, {
        format_prices, premiums_apply: ovr.premiums_apply, active: ovr.active && Object.keys(format_prices).length > 0, notes: ovr.notes,
      });
      toast.success("Dealer-specific pricing saved — audited");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  if (!data) return <div className="flex min-h-[30vh] items-center justify-center" data-testid="dealer-pricing-loading"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div>;

  const ev = data.evaluation || {};
  const ps = data.partner_status || {};

  return (
    <div className="space-y-6" data-testid="dealer-pricing-tab">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="border-border bg-white">
          <CardContent className="p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Partner status</p>
            <p className="mt-3 font-display text-3xl font-light text-charcoal" data-testid="partner-status-current">{ps.status || "STANDARD"}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {ps.effective_from ? `Effective ${new Date(ps.effective_from).toLocaleDateString()}` : "Default status"}
              {ps.review_at ? ` · review ${new Date(ps.review_at).toLocaleDateString()}` : ""}
            </p>
            <div className="mt-5 space-y-3">
              <div className="space-y-1">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">Set status</Label>
                <Select value={statusForm.status} onValueChange={(v) => setStatusForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="partner-status-select" className="h-11 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Input value={statusForm.reason} onChange={(e) => setStatusForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Reason (audited)" data-testid="partner-status-reason" className="h-11 bg-white focus-visible:ring-terracotta" />
              <Button onClick={saveStatus} disabled={saving} data-testid="partner-status-save" className="bg-terracotta text-white hover:bg-terracotta-hover">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply status"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border bg-white">
          <CardContent className="p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Quarterly evaluation (recommendation only)</p>
            <div className="mt-4 grid grid-cols-2 gap-4" data-testid="status-evaluation">
              <div><p className="num text-2xl font-light text-charcoal">{(ev.eligible_sqft ?? 0).toLocaleString()} sq ft</p><p className="text-xs text-muted-foreground">Purchased {ev.quarter}</p></div>
              <div><p className="num text-2xl font-light text-charcoal">{(ev.avg_monthly_sqft ?? 0).toLocaleString()} sq ft</p><p className="text-xs text-muted-foreground">Avg / month</p></div>
              <div><p className="num text-2xl font-light text-charcoal" data-testid="recommended-status">{ev.recommended_status}</p><p className="text-xs text-muted-foreground">Recommended</p></div>
              <div><p className="num text-2xl font-light text-charcoal">{ev.next_status ? `${ev.remaining_sqft_to_next?.toLocaleString()} sq ft` : "—"}</p><p className="text-xs text-muted-foreground">{ev.next_status ? `Remaining to ${ev.next_status}` : "Top status"}</p></div>
            </div>
            <div className="mt-4 border-t border-border pt-3 text-xs text-warmgrey" data-testid="status-thresholds">
              {STATUSES.slice(1).map((s) => (
                <p key={s} className="flex justify-between py-0.5"><span>{s}</span><span className="font-mono">{ev.thresholds?.[s]?.toLocaleString()} sq ft / quarter</span></p>
              ))}
            </div>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground" data-testid="status-vs-volume-note">
              These quarterly thresholds qualify an earned Partner Status. They are separate from per-order volume qualification — a single large order can unlock a better price immediately (Admin → Pricing). Status changes are always reviewed and applied by an admin; nothing changes automatically.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border bg-white">
        <CardContent className="p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-charcoal">Dealer-specific pricing</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Overrides the standard pricing schedule for this account. Premiums {ovr.premiums_apply ? "continue to apply on top" : "do NOT apply (override replaces the final price)"}.
          </p>
          <div className="mt-4 flex flex-wrap items-end gap-4" data-testid="dealer-override-form">
            {FORMATS.map((f) => (
              <div key={f} className="space-y-1">
                <Label className="text-xs font-semibold uppercase tracking-wider text-warmgrey">{FMT_LABEL[f]} $/box</Label>
                <Input type="number" step="0.01" min="0" value={ovr.prices[f] ?? ""} placeholder="std"
                  onChange={(e) => setOvr((o) => ({ ...o, prices: { ...o.prices, [f]: e.target.value } }))}
                  data-testid={`override-price-${f}`} className="h-11 w-32 bg-white focus-visible:ring-terracotta" />
              </div>
            ))}
            <label className="flex items-center gap-2 pb-2 text-xs font-medium text-charcoal">
              <Switch checked={ovr.premiums_apply} onCheckedChange={(v) => setOvr((o) => ({ ...o, premiums_apply: v }))} data-testid="override-premiums-apply" />
              Premiums apply
              <HelpTip testid="help-premiums-apply" text="ON: color premiums (e.g. MZ004) are added on top of these dealer-specific prices. OFF: the prices below are the final per-box prices for this account." />
            </label>
            <label className="flex items-center gap-2 pb-2 text-xs font-medium text-charcoal">
              <Switch checked={ovr.active} onCheckedChange={(v) => setOvr((o) => ({ ...o, active: v }))} data-testid="override-active" />
              Active
              <HelpTip testid="help-override-active" text="ON: these dealer-specific prices replace the standard pricing schedule on future orders for this account. Historical orders are never recalculated." />
            </label>
          </div>
          <div className="mt-3 max-w-lg">
            <Input value={ovr.notes} onChange={(e) => setOvr((o) => ({ ...o, notes: e.target.value }))} placeholder="Internal notes (never dealer-visible)" data-testid="override-notes" className="h-11 bg-white focus-visible:ring-terracotta" />
          </div>
          <div className="mt-4">
            <Button onClick={saveOverride} disabled={saving} data-testid="override-save" className="bg-terracotta text-white hover:bg-terracotta-hover">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save dealer pricing"}
            </Button>
          </div>
        </CardContent>
      </Card>
      <PartnerBenefitsPanel dealerId={dealerId} refreshKey={ps.updated_at || ps.status} />
    </div>
  );
}
